import Phaser from "phaser";
import type { ActiveVote, VoteOption } from "../transport/EventTypes";
import type { GameApp, VoteCtx } from "../app/GameApp";
import { REGISTRY_KEYS } from "../app/GameApp";
import { mountOverlay, type DomOverlay } from "@/ui/domHelpers";

const BASE_URL = import.meta.env.VITE_GAME_SERVICE_URL || "/api/game";

export class VotingOverlay extends Phaser.Scene {
  private app!: GameApp;
  private overlay?: DomOverlay;

  constructor() {
    super("VotingOverlay");
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;
    const initial = this.registry.get(REGISTRY_KEYS.currentVote) as VoteCtx | null;
    if (initial) this.render(initial.vote);

    const onChange = (_parent: unknown, value: VoteCtx | null) => {
      if (value) this.render(value.vote);
    };
    this.registry.events.on(`changedata-${REGISTRY_KEYS.currentVote}`, onChange);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.registry.events.off(`changedata-${REGISTRY_KEYS.currentVote}`, onChange);
    });
  }

  private render(vote: ActiveVote) {
    this.children.removeAll(true);

    const alreadyVoted = !!vote.myChoice;
    const chosen = alreadyVoted
      ? vote.options.find((o) => o.id === vote.myChoice)
      : null;

    let bodyHtml: string;
    if (alreadyVoted) {
      bodyHtml = `
        <p class="voting__question">${vote.question}</p>
        <p class="voting__result">Ты выбрал: <strong>${chosen?.label ?? vote.myChoice}</strong></p>
        <div class="event__actions">
          <button class="dom-btn dom-btn--primary" id="vote-close">Закрыть</button>
        </div>
      `;
    } else {
      const optionsHtml = vote.options.map((opt, i) => `
        <label class="voting__option-row">
          <input type="radio" name="vote-option" value="${opt.id}" ${i === 0 ? "checked" : ""} />
          <span>${opt.label}</span>
        </label>
      `).join("");
      bodyHtml = `
        <p class="voting__question">${vote.question}</p>
        <div class="voting__options">${optionsHtml}</div>
        <div class="event__actions">
          <button class="dom-btn dom-btn--primary" id="vote-submit">Подтвердить</button>
        </div>
      `;
    }

    this.overlay = mountOverlay(this, {
      title: "Голосование",
      bodyHtml,
      onClose: () => this.app.completeCurrentEvent(),
    });

    if (alreadyVoted) {
      this.overlay.root.querySelector("#vote-close")?.addEventListener("click", () => {
        this.app.completeCurrentEvent();
      });
    } else {
      this.overlay.root.querySelector("#vote-submit")?.addEventListener("click", () => {
        const checked = this.overlay!.root.querySelector<HTMLInputElement>('input[name="vote-option"]:checked');
        if (!checked) return;
        const opt = vote.options.find((o) => o.id === checked.value);
        if (opt) this.submitVote(vote.id, opt);
      });
    }
  }

  private async submitVote(voteId: string, option: VoteOption) {
    try {
      const res = await fetch(`${BASE_URL}/votes/${voteId}/submit`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ optionId: option.id }),
      });
      const data = await res.json();
      if (data.ok) {
        await this.app.getTransport().refreshState?.();
        this.app.completeCurrentEvent();
      } else {
        console.warn("[VotingOverlay] submit failed:", data.reason);
        alert("Не удалось проголосовать: " + (data.reason || "unknown"));
      }
    } catch (e) {
      console.error("[VotingOverlay] submit error:", e);
      alert("Ошибка сети при голосовании");
    }
  }

  shutdown() {
    this.overlay = undefined;
  }
}
